import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { BucketListItem } from '../types';
import { api } from '../api';
import BucketItemComponent from '../components/BucketItem';
import CompletionModal from '../components/CompletionModal';

/**
 * Container for the bucket list with soft, dreamy styling and cute aesthetics
 */
const BucketListContainer = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border-radius: 30px;
  padding: 3rem 2.5rem;
  backdrop-filter: blur(20px);
  border: 3px solid rgba(255, 179, 217, 0.3);
  box-shadow: 0 20px 40px rgba(255, 179, 217, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  position: relative;

  h2 {
    text-align: center;
    font-size: 3.2rem;
    margin-bottom: 0.5rem;
    font-family: 'Fredoka One', cursive;
    background: linear-gradient(135deg, #ff69b4, #da70d6, #ff1493, #ba55d3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 2px 2px 8px rgba(255, 105, 180, 0.4);
    letter-spacing: 0.5px;
    transform: rotate(-1deg);
    position: relative;

    &::after {
      content: '♡';
      position: absolute;
      right: -0.5em;
      top: -0.2em;
      font-size: 0.6em;
      color: #ff69b4;
      text-shadow: 1px 1px 3px rgba(255, 105, 180, 0.5);
      animation: heartbeat 2s ease-in-out infinite;
    }

    @keyframes heartbeat {
      0%,
      100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
    }
  }

  > p {
    text-align: center;
    font-size: 1.3rem;
    color: #8b5a8c;
    margin-bottom: 2.5rem;
    font-style: italic;
    font-weight: 400;
    font-family: 'Caveat', cursive;
    line-height: 1.4;
    transform: rotate(0.5deg);
    position: relative;

    &::before,
    &::after {
      content: '✦';
      color: #da70d6;
      font-size: 0.8em;
      opacity: 0.7;
      position: relative;
      top: -2px;
    }

    &::before {
      margin-right: 10px;
    }

    &::after {
      margin-left: 10px;
    }
  }
`;

/**
 * Loading state styling with cute animation
 */
const LoadingMessage = styled.div`
  text-align: center;
  font-size: 1.6rem;
  padding: 3rem;
  color: #da70d6;
  font-family: 'Nunito', sans-serif;
  font-weight: 600;

  &::after {
    content: '♡';
    margin-left: 15px;
    font-family: 'Fredoka One', cursive;
    color: #ff69b4;
    animation: float 1.5s ease-in-out infinite;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) rotate(0deg);
    }
    50% {
      transform: translateY(-10px) rotate(5deg);
    }
  }
`;

/**
 * Error message styling with soft pink/red aesthetic
 */
const ErrorContainer = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 182, 193, 0.3),
    rgba(255, 192, 203, 0.2)
  );
  border: 2px solid rgba(255, 105, 180, 0.3);
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: #8b0026;
  text-align: center;
  box-shadow: 0 10px 20px rgba(255, 105, 180, 0.1);

  p {
    margin-bottom: 1rem;
    font-weight: 500;
    font-family: 'Quicksand', sans-serif;
  }
`;

/**
 * Primary button styling with cute, soft aesthetics
 */
const PrimaryButton = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(45deg, #ff69b4, #da70d6);
  color: white;
  font-family: 'Quicksand', sans-serif;
  box-shadow: 0 5px 15px rgba(255, 105, 180, 0.3), 0 2px 5px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: linear-gradient(45deg, #ff1493, #ba55d3);
    box-shadow: 0 8px 25px rgba(255, 105, 180, 0.4),
      0 4px 10px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px) scale(1.02);
  }
`;

const BucketListView: React.FC = () => {
  const [items, setItems] = useState<BucketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompletedItem, setSelectedCompletedItem] =
    useState<BucketListItem | null>(null);

  useEffect(() => {
    loadItems();
  }, []);

  const handleCompletedClick = (item: BucketListItem) => {
    setSelectedCompletedItem(item);
  };

  const closeModal = () => {
    setSelectedCompletedItem(null);
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await api.getBucketListItems();
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const buildHierarchy = (
    items: BucketListItem[]
  ): {
    parents: BucketListItem[];
    childrenMap: Map<string, BucketListItem[]>;
  } => {
    const parents = items.filter(item => !item.parentId);
    const childrenMap = new Map<string, BucketListItem[]>();

    items.forEach(item => {
      if (item.parentId) {
        if (!childrenMap.has(item.parentId)) {
          childrenMap.set(item.parentId, []);
        }
        childrenMap.get(item.parentId)!.push(item);
      }
    });

    // Sort children by order
    childrenMap.forEach(children => {
      children.sort((a, b) => a.order - b.order);
    });

    // Sort parents by order
    parents.sort((a, b) => a.order - b.order);

    return { parents, childrenMap };
  };

  if (loading) {
    return <LoadingMessage>loading aria's bucket list...</LoadingMessage>;
  }

  if (error) {
    return (
      <ErrorContainer>
        <p>Error: {error}</p>
        <PrimaryButton onClick={loadItems}>Retry</PrimaryButton>
      </ErrorContainer>
    );
  }

  const { parents, childrenMap } = buildHierarchy(items);

  return (
    <>
      <BucketListContainer>
        <h2>things i want to do before i die</h2>
        <p>one day, i'm going to look back on this list and smile.</p>

        {parents.length === 0 ? (
          <p>No items in the bucket list yet.</p>
        ) : (
          parents.map(item => (
            <BucketItemComponent
              key={item.id}
              item={item}
              children={childrenMap.get(item.id) || []}
              onCompletedClick={handleCompletedClick}
            />
          ))
        )}
      </BucketListContainer>

      {selectedCompletedItem && (
        <CompletionModal
          item={selectedCompletedItem}
          isOpen={!!selectedCompletedItem}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default BucketListView;
